import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import type { ControlPosition, MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'
import Map, { Marker, GeolocateControl } from 'react-map-gl/mapbox'
import styled from 'styled-components'
import { rem } from 'polished'
import { ChatMapContext, findMarkerByKey } from './ChatMapContext'
import type { ChatMapMarker, ChatMapViewState } from './types'
import { MapboxGlobalStyles } from './styles'
import { MapboxAttributionControl } from 'components/Common/Mapbox/MapboxAttributionControl'
import MapStyleToggleControl from './MapStyleToggleControl'
import MapZoomControl from './MapZoomControl'
import MapFullscreenControl from './MapFullscreenControl'
import ChatMapMarkerContent from 'tripPlanner/components/Chat/ChatMapMarkerContent'
import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import {
  DEFAULT_MAP_STYLE_MODE,
  getMapboxConfig,
  getMapboxStyle,
  MAP_FLAT_CAMERA,
  type MapStyleMode,
} from './map3DConfig'
import { useMap3DStyle } from './useMap3DStyle'
import { useMapLightPreset } from './useMapLightPreset'
import { useMapResize } from 'tripPlanner/components/Chat/useMapResize'
import { createMapBounds, mapBoundsToMapboxBounds } from 'lib/maps/bounds'
import {
  MARKER_TYPE_SUGGESTED,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'

const DEFAULT_VIEW_STATE: ChatMapViewState = {
  longitude: 0,
  latitude: 20,
  zoom: 1.5,
}

const FOCUSED_ZOOM = 14
const FIT_BOUNDS_MAX_ZOOM = 15
const FIT_BOUNDS_ZOOM_EPSILON = 0.15

const DEFAULT_FIT_BOUNDS_PADDING = {
  top: 50,
  bottom: 50,
  left: 50,
  right: 50,
} as const

type FitBoundsPadding = {
  top: number
  bottom: number
  left: number
  right: number
}

type MapInstance = NonNullable<ReturnType<MapRef['getMap']>>
type CameraMode = 'controlled' | 'uncontrolled'

function cameraCenterToLngLat(center: unknown): { lng: number; lat: number } | undefined {
  if (center == null) return undefined
  if (Array.isArray(center) && typeof center[0] === 'number' && typeof center[1] === 'number') {
    return { lng: center[0], lat: center[1] }
  }
  if (
    typeof center === 'object' &&
    center !== null &&
    'lng' in center &&
    'lat' in center &&
    typeof (center as { lng: unknown }).lng === 'number' &&
    typeof (center as { lat: unknown }).lat === 'number'
  ) {
    return { lng: (center as { lng: number }).lng, lat: (center as { lat: number }).lat }
  }
  return undefined
}

/**
 * Fit markers with Mapbox's measured canvas. When `setViewState` is provided
 * (controlled mode), sync React so the Map does not snap back.
 */
function jumpToFitMarkers(args: {
  map: MapInstance
  markers: Array<ChatMapMarker>
  durationMs: number
  fitBoundsPadding: FitBoundsPadding
  setViewState?: (next: ChatMapViewState) => void
}): void {
  const { map, markers, durationMs, fitBoundsPadding, setViewState } = args
  const mapBounds = createMapBounds(markers)
  if (!mapBounds) return
  const bounds = mapBoundsToMapboxBounds(mapBounds)

  const [[minLng, minLat], [maxLng, maxLat]] = bounds
  const pitchCamera = MAP_FLAT_CAMERA

  if (minLng === maxLng && minLat === maxLat) {
    const next: ChatMapViewState = {
      longitude: minLng,
      latitude: minLat,
      zoom: FOCUSED_ZOOM,
      ...pitchCamera,
    }
    if (durationMs <= 0) {
      map.jumpTo({ center: [minLng, minLat], zoom: FOCUSED_ZOOM, ...pitchCamera })
    } else {
      map.easeTo({ center: [minLng, minLat], zoom: FOCUSED_ZOOM, ...pitchCamera, duration: durationMs })
    }
    setViewState?.(next)
    return
  }

  const padding = { ...fitBoundsPadding }

  if (durationMs <= 0) {
    const camera = map.cameraForBounds(bounds, {
      padding,
      maxZoom: FIT_BOUNDS_MAX_ZOOM,
    })
    const center = cameraCenterToLngLat(camera?.center)
    if (!camera || center == null || typeof camera.zoom !== 'number') {
      map.fitBounds(bounds, { padding, maxZoom: FIT_BOUNDS_MAX_ZOOM, ...pitchCamera, duration: 0 })
      if (setViewState) {
        const mapCenter = map.getCenter()
        setViewState({
          longitude: mapCenter.lng,
          latitude: mapCenter.lat,
          zoom: Math.max(map.getZoom() - FIT_BOUNDS_ZOOM_EPSILON, 1),
          ...pitchCamera,
        })
      }
      return
    }

    const zoom = Math.max(camera.zoom - FIT_BOUNDS_ZOOM_EPSILON, 1)
    const next: ChatMapViewState = {
      longitude: center.lng,
      latitude: center.lat,
      zoom,
      ...pitchCamera,
    }
    map.jumpTo({
      center: [center.lng, center.lat],
      zoom,
      ...pitchCamera,
    })
    setViewState?.(next)
    return
  }

  map.fitBounds(bounds, {
    padding,
    maxZoom: FIT_BOUNDS_MAX_ZOOM,
    ...pitchCamera,
    duration: durationMs,
  })
}

export interface MapMarkerRect {
  top: number
  bottom: number
  left: number
  right: number
  width: number
  height: number
}

interface ChatMapProps {
  markers: Array<ChatMapMarker>
  className?: string
  onMarkerClick?: (placeId: string, markerRect?: MapMarkerRect) => void
  onMarkerMouseEnter?: (placeId: string, markerRect: MapMarkerRect) => void
  onMarkerMouseLeave?: () => void
  activePlaceId?: string | null
  onActivePlaceIdChange?: (placeId: string | null) => void
  scrollZoom?: boolean
  initialCenterPlaceId?: string | null
  initialViewState?: Partial<ChatMapViewState>
  /**
   * `controlled` — React owns camera (drawers that fly to an active place).
   * `uncontrolled` — Mapbox owns camera after mount; fits are imperative only
   * (in-chat card). Matches ChatMapWidget.
   */
  cameraMode?: CameraMode
  skipInitialFitBounds?: boolean
  /** Duration (ms) for the one-shot post-load fitBounds. Use `0` to jump. */
  fitBoundsDurationMs?: number
  /** Padding passed to Mapbox fitBounds / cameraForBounds. */
  fitBoundsPadding?: FitBoundsPadding
  showGeolocateControl?: boolean
  showAttributionControl?: boolean
  mapStyleMode?: MapStyleMode
  onMapStyleModeChange?: (mode: MapStyleMode) => void
  showZoomControl?: boolean
  styleTogglePosition?: ControlPosition
  showFullscreenControl?: boolean
  onEnlarge?: () => void
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  .mapboxgl-ctrl-group {
    border-start-start-radius: ${rem(8)};
    border-start-end-radius: ${rem(8)};
    border-end-start-radius: ${rem(8)};
    border-end-end-radius: ${rem(8)};
    box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.15);
  }

  .mapboxgl-ctrl-group button:first-child {
    border-start-start-radius: ${rem(8)};
    border-start-end-radius: ${rem(8)};
    border-end-end-radius: 0;
    border-end-start-radius: 0;
  }

  .mapboxgl-ctrl-group button:last-child {
    border-start-start-radius: 0;
    border-start-end-radius: 0;
    border-end-end-radius: ${rem(8)};
    border-end-start-radius: ${rem(8)};
  }

  .mapboxgl-ctrl-group button:only-child {
    border-start-start-radius: ${rem(8)};
    border-start-end-radius: ${rem(8)};
    border-end-start-radius: ${rem(8)};
    border-end-end-radius: ${rem(8)};
  }
`

const MarkerButton = styled.div`
  cursor: pointer;
`

function toSuggestedMapMarker(marker: ChatMapMarker): MapMarkerComponent {
  return {
    lat: marker.lat,
    lng: marker.lng,
    placeId: marker.markerKey,
    markerId: marker.markerKey,
    label: marker.label ?? marker.name,
    markerType: MARKER_TYPE_SUGGESTED,
  }
}

function ChatMap({
  markers,
  className,
  onMarkerClick,
  onMarkerMouseEnter,
  onMarkerMouseLeave,
  activePlaceId: controlledActivePlaceId,
  onActivePlaceIdChange,
  scrollZoom = true,
  initialCenterPlaceId,
  initialViewState,
  cameraMode = 'controlled',
  skipInitialFitBounds = false,
  fitBoundsDurationMs = 500,
  fitBoundsPadding = DEFAULT_FIT_BOUNDS_PADDING,
  showGeolocateControl = false,
  showAttributionControl = true,
  mapStyleMode: controlledMapStyleMode,
  onMapStyleModeChange,
  showZoomControl = true,
  styleTogglePosition = 'top-right',
  showFullscreenControl = true,
  onEnlarge,
}: ChatMapProps) {
  const intl = useIntl()
  const mapRef = useRef<MapRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isControlledCamera = cameraMode === 'controlled'
  const resolvedInitialViewState = useMemo(() => ({
    ...DEFAULT_VIEW_STATE,
    ...initialViewState,
  }), [initialViewState])
  const [viewState, setViewState] = useState<ChatMapViewState>(resolvedInitialViewState)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [internalMapStyleMode, setInternalMapStyleMode] = useState<MapStyleMode>(DEFAULT_MAP_STYLE_MODE)
  const [internalActivePlaceId, setInternalActivePlaceId] = useState<string | null>(null)
  const hasInitialFitBounds = useRef(skipInitialFitBounds)
  const shouldFlyToActivePlaceRef = useRef(true)
  const mapStyleMode = controlledMapStyleMode ?? internalMapStyleMode
  const setMapStyleMode = onMapStyleModeChange ?? setInternalMapStyleMode
  const lightPreset = useMapLightPreset({ enabled: mapStyleMode === DEFAULT_MAP_STYLE_MODE })
  useMap3DStyle({ mapRef, isMapLoaded, lightPreset })
  useMapResize(mapRef, containerRef)

  const activePlaceId = controlledActivePlaceId !== undefined
    ? controlledActivePlaceId
    : internalActivePlaceId

  const setActivePlaceId = useCallback((placeId: string | null) => {
    if (onActivePlaceIdChange) {
      onActivePlaceIdChange(placeId)
    } else {
      setInternalActivePlaceId(placeId)
    }
  }, [onActivePlaceIdChange])

  useEffect(() => {
    if (!isMapLoaded || hasInitialFitBounds.current || markers.length === 0) {
      return
    }

    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }

    hasInitialFitBounds.current = true
    // Resize first so cameraForBounds uses the real canvas size.
    map.resize()
    jumpToFitMarkers({
      map,
      markers,
      durationMs: fitBoundsDurationMs,
      fitBoundsPadding,
      setViewState: isControlledCamera ? setViewState : undefined,
    })
  }, [fitBoundsDurationMs, fitBoundsPadding, isControlledCamera, isMapLoaded, markers])

  const focusPlace = useCallback((markerKey: string) => {
    const marker = findMarkerByKey(markers, markerKey)
    if (!marker) {
      return
    }

    if (isControlledCamera) {
      setViewState(prev => ({
        ...prev,
        longitude: marker.lng,
        latitude: marker.lat,
        zoom: FOCUSED_ZOOM,
        ...MAP_FLAT_CAMERA,
      }))
    } else {
      mapRef.current?.getMap()?.jumpTo({
        center: [marker.lng, marker.lat],
        zoom: FOCUSED_ZOOM,
        ...MAP_FLAT_CAMERA,
      })
    }

    shouldFlyToActivePlaceRef.current = false
    setActivePlaceId(markerKey)
  }, [isControlledCamera, markers, setActivePlaceId])

  useEffect(() => {
    // In-chat cards use uncontrolled camera — pin clicks should open details,
    // not re-frame the map. Controlled surfaces (drawers/lists) fly when the
    // parent drives activePlaceId.
    if (!isControlledCamera) {
      return
    }

    if (!activePlaceId || !shouldFlyToActivePlaceRef.current) {
      shouldFlyToActivePlaceRef.current = true
      return
    }

    const marker = findMarkerByKey(markers, activePlaceId)
    if (marker) {
      const map = mapRef.current?.getMap()
      if (map) {
        map.flyTo({
          center: [marker.lng, marker.lat],
          zoom: FOCUSED_ZOOM,
          ...MAP_FLAT_CAMERA,
          duration: 500,
        })
      }
    }
  }, [activePlaceId, isControlledCamera, markers])

  const convertRectToMarkerRect = useCallback((rect?: DOMRect): MapMarkerRect | undefined => {
    if (!rect) {
      return undefined
    }
    return {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    }
  }, [])

  const handleMarkerClick = useCallback((markerKey: string, markerRect?: DOMRect) => {
    shouldFlyToActivePlaceRef.current = false
    setActivePlaceId(markerKey)
    onMarkerClick?.(markerKey, convertRectToMarkerRect(markerRect))
  }, [convertRectToMarkerRect, onMarkerClick, setActivePlaceId])

  const handleMarkerMouseEnter = useCallback((markerKey: string, markerRect: DOMRect) => {
    const rect = convertRectToMarkerRect(markerRect)
    if (!rect) return
    onMarkerMouseEnter?.(markerKey, rect)
  }, [convertRectToMarkerRect, onMarkerMouseEnter])

  const initialCenterRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialCenterPlaceId) {
      return
    }
    if (initialCenterRef.current === initialCenterPlaceId) {
      return
    }
    initialCenterRef.current = initialCenterPlaceId
    focusPlace(initialCenterPlaceId)
  }, [initialCenterPlaceId, focusPlace])

  const handleMove = useCallback((evt: ViewStateChangeEvent) => {
    if (!isControlledCamera) return
    setViewState({
      longitude: evt.viewState.longitude,
      latitude: evt.viewState.latitude,
      zoom: evt.viewState.zoom,
      pitch: evt.viewState.pitch,
      bearing: evt.viewState.bearing,
    })
  }, [isControlledCamera])

  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true)
    // Chat bubbles / drawers can size after first paint; force a remeasure
    // so the canvas fills the container instead of leaving a bottom gap.
    mapRef.current?.getMap()?.resize()
  }, [])

  const handleMarkerButtonClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const markerKey = event.currentTarget.dataset.markerKey
    if (!markerKey) return
    handleMarkerClick(markerKey, event.currentTarget.getBoundingClientRect())
  }, [handleMarkerClick])

  const handleMarkerButtonMouseEnter = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!onMarkerMouseEnter) return
    const markerKey = event.currentTarget.dataset.markerKey
    if (!markerKey) return
    handleMarkerMouseEnter(markerKey, event.currentTarget.getBoundingClientRect())
  }, [handleMarkerMouseEnter, onMarkerMouseEnter])

  const handleMarkerButtonMouseLeave = useCallback(() => {
    onMarkerMouseLeave?.()
  }, [onMarkerMouseLeave])

  const handleMarkerButtonKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    const markerKey = event.currentTarget.dataset.markerKey
    if (!markerKey) return
    handleMarkerClick(markerKey, event.currentTarget.getBoundingClientRect())
  }, [handleMarkerClick])

  const contextValue = useMemo(() => ({
    activePlaceId,
    setActivePlaceId,
    focusPlace,
    markers,
  }), [activePlaceId, setActivePlaceId, focusPlace, markers])

  // Keep MapMarkerComponent refs stable so memo(ChatMapMarkerContent) can skip unchanged pins.
  const suggestedMarkers = useMemo(
    () => markers.map((marker) => ({
      marker,
      mapMarker: toSuggestedMapMarker(marker),
    })),
    [markers],
  )

  const viewPlaceDetailsLabel = intl.formatMessage({
    id: 'XbK5JyHgvgONnlMFkuLT',
    defaultMessage: 'View place details',
    description: 'Fallback accessible label for a map marker button in the chat map',
  })

  const mapboxAccessToken = getMapboxAccessToken(MAPBOX_ACCESS_TOKEN_USE_CASES.tripPlanner)

  if (!mapboxAccessToken) {
    return (
      <Container ref={containerRef} className={className}>
        <div style={{ padding: 20, textAlign: 'center' }}>
          {intl.formatMessage({
            id: 'N1wlozfCxNO0uKUi1ATr',
            defaultMessage: 'Map unavailable - missing configuration',
            description: 'Fallback message for missing mapbox configuration in the trip planner AI chat',
          })}
        </div>
      </Container>
    )
  }

  return (
    <ChatMapContext.Provider value={contextValue}>
      <Container ref={containerRef} className={className}>
        <MapboxGlobalStyles />
        <Map
          ref={mapRef}
          mapboxAccessToken={mapboxAccessToken}
          {...(isControlledCamera
            ? { ...viewState, onMove: handleMove }
            : { initialViewState: resolvedInitialViewState })}
          onLoad={handleMapLoad}
          style={{ width: '100%', height: '100%' }}
          mapStyle={getMapboxStyle(mapStyleMode)}
          config={getMapboxConfig(lightPreset)}
          scrollZoom={scrollZoom}
          attributionControl={false}
          language={intl.locale}
          projection="globe"
        >
          {showAttributionControl && <MapboxAttributionControl />}

          {suggestedMarkers.map(({ marker, mapMarker }) => {
            const isActive = marker.markerKey === activePlaceId
            return (
              <Marker
                key={marker.markerKey}
                longitude={marker.lng}
                latitude={marker.lat}
                anchor="center"
              >
                <MarkerButton
                  role="button"
                  tabIndex={0}
                  data-marker-key={marker.markerKey}
                  aria-label={marker.label ?? marker.name ?? viewPlaceDetailsLabel}
                  onClick={handleMarkerButtonClick}
                  onMouseEnter={handleMarkerButtonMouseEnter}
                  onMouseLeave={handleMarkerButtonMouseLeave}
                  onKeyDown={handleMarkerButtonKeyDown}
                >
                  <ChatMapMarkerContent
                    marker={mapMarker}
                    deemphasizeBaseMarkers={false}
                    isHighlighted={isActive}
                  />
                </MarkerButton>
              </Marker>
            )
          })}

          {showFullscreenControl && (
            <MapFullscreenControl position="top-right" onEnlarge={onEnlarge} />
          )}
          <MapStyleToggleControl
            position={styleTogglePosition}
            mapStyleMode={mapStyleMode}
            onMapStyleModeChange={setMapStyleMode}
          />
          {showZoomControl && <MapZoomControl position="bottom-right" />}
          {showGeolocateControl && GeolocateControl && <GeolocateControl position="top-right" />}
        </Map>
      </Container>
    </ChatMapContext.Provider>
  )
}

export default ChatMap
